import {
	Flex,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	useToast,
} from '@chakra-ui/react';
import { IconDownload } from '@tabler/icons';
import ExportGenericCSVButton, {
	Module,
} from 'components/buttons/exportButtons/ExportGenericCSVButton';
import { LoadingSpinner } from 'components/loading/Loading';

import {
	getAsyncExportProcessType,
	getCollaborationTypeFromProcessType,
} from '../../../utils/asyncExport/exportTypeMapper';
import { AsyncExportButton } from './AsyncExportButton';
import { ExportButtonMessages } from './variables/exportButtonMessages';
export interface IMenuItemExportProps<Params> {
	dataTest?: string;
	label: string;
	module: Module;
	filters:
		| {
				cycle: string;
				year: string;
		  }
		| undefined;
	params: Params;
	errorDescription?: string;
	agGrid?: boolean;
	nameExport: string;
	exportAll?: boolean;
}
export interface IExportMenuClick {
	label: string;
	onClick: () => void;
}
const MenuItemExport = <Params,>(
	props: IMenuItemExportProps<Params> | IExportMenuClick
) => {
	const toast = useToast();
	const { label } = props;
	if ('onClick' in props) {
		return (
			<MenuItem
				onClick={() => {
					try {
						props.onClick();

						toast({
							title: 'Sucesso!',
							description:
								ExportButtonMessages.MSG_EXPORT_SUCCESS,
							status: 'success',
							duration: 8000,
							isClosable: true,
							position: 'bottom-left',
						});
					} catch (error) {
						console.error(error);
					}
				}}
			>
				{props.label}
			</MenuItem>
		);
	}

	const processType = getAsyncExportProcessType(
		props.module,
		props.exportAll,
		props.params as Record<string, unknown>
	);

	if (processType && props.exportAll) {
		const params = props.params as {
			cycle_id?: number;
			company_area_id?: number;
			collaboration_type?: string;
		};

		const collaborationTypeFromParams = params?.collaboration_type as
			| 'planning'
			| 'marketing'
			| 'commercial'
			| undefined;

		const collaborationTypeFromProcessType =
			getCollaborationTypeFromProcessType(processType);

		const collaborationType =
			collaborationTypeFromParams || collaborationTypeFromProcessType;

		return (
			<AsyncExportButton
				processType={processType}
				cycleId={params?.cycle_id}
				companyAreaId={params?.company_area_id}
				collaborationType={collaborationType}
				onClick={() => {}}
			>
				<MenuItem data-test={props.dataTest}>
					<Flex gap={'4px'}>
						<Flex grow={1}>{label}</Flex>
					</Flex>
				</MenuItem>
			</AsyncExportButton>
		);
	}

	return (
		<ExportGenericCSVButton
			nameExport={props.nameExport}
			module={props.module}
			params={props.params}
			errorDescription={props.errorDescription}
			agGrid={props.agGrid}
			exportAll={props.exportAll}
			Component={({ onClick, isDisabled, isLoading }) => (
				<MenuItem
					data-test={props.dataTest}
					onClick={onClick}
					isDisabled={isDisabled}
				>
					<Flex gap={'4px'}>
						<Flex grow={1}>{label}</Flex>
						<Flex display={isLoading ? 'flex' : 'none'}>
							<LoadingSpinner
								message=''
								size={'sm'}
								label='Carregando dados...'
							/>
						</Flex>
					</Flex>
				</MenuItem>
			)}
		/>
	);
};

interface IExportCSVButtonProps<Params> {
	menuList: (IMenuItemExportProps<Params> | IExportMenuClick)[];
	agGrid?: boolean;
}
export default function ExportCSVMenuButton<Params>({
	menuList,
	agGrid = false,
}: IExportCSVButtonProps<Params>) {
	return (
		<Menu>
			<MenuButton
				as={IconButton}
				aria-label='Export options'
				data-test='button-exportar'
				variant='solid'
				color='easyBLUE.300'
				borderRadius={'15px'}
				border={'1px solid'}
				borderColor={'easyBrandMenu.200'}
				icon={<IconDownload />}
			/>
			<MenuList
				fontSize={'medium'}
				fontWeight='light'
				color='gray.600'
				zIndex={3}
			>
				{menuList.map(p => (
					<MenuItemExport
						{...p}
						key={p.label}
						agGrid={agGrid}
					/>
				))}
			</MenuList>
		</Menu>
	);
}
